import React from "react";
import { FiCompass, FiFileText, FiEdit3, FiAlertTriangle, FiLoader } from "react-icons/fi";
import { PricingCard as Card, Label, I, S, InputShell, Help, FieldError, fieldClass } from "./fields";

const PAYMENT_METHODS = ["CASH", "UPI", "CARD"];

const hasCoords = (lat, lng) => lat !== "" && lat != null && lng !== "" && lng != null;

function RouteEnd({ label, address, fallback, pinned }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-sm mt-0.5 break-words ${address ? "text-slate-800 dark:text-slate-100" : "text-slate-400 dark:text-slate-500"}`}>
        {address || fallback}
      </div>
      {address && !pinned && (
        <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">Not pinned on the map</div>
      )}
    </div>
  );
}

export default function PricingSection({
  v,
  setV,
  set,
  errors,
  readOnly,
  activeTab,
  fareBreakdown,
  fareStatus,
  onApplyPlanFare,
  distanceLoading,
  distanceError,
  routeEstimate,
  isRoundTrip,
  onRecalculate,
  initial,

  uploadDutySlip,
  uploadParkingSlip,
  FileUploadComponent,
}) {
  const pickupPinned = hasCoords(v.pickupLat, v.pickupLng);
  const dropPinned = hasCoords(v.dropLat, v.dropLng);
  const pickupText = v.pickupGoogleAddr || (v.pickupMode === "AIRPORT" ? v.pickupAirportName : v.pickupAddress);
  const dropText = v.dropGoogleAddr || (v.dropMode === "AIRPORT" ? v.dropAirportName : v.dropAddress);

  return (
    <>
      <Card
        title="Distance & Duration"
        subtitle="Estimated from the Google Map locations picked above. Straight-line distance; road distance may differ."
        icon={<FiCompass className="w-5 h-5" />}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <RouteEnd label="From" address={pickupText} pinned={pickupPinned} fallback="Choose the pickup under Pickup Details" />
            <RouteEnd label="To" address={dropText} pinned={dropPinned} fallback="Choose the drop under Drop Details" />
          </div>

          {!readOnly && (
            routeEstimate ? (
              <div className="text-xs text-slate-600 dark:text-slate-300">
                Estimate: <span className="font-semibold">{routeEstimate.km.toFixed(1)} km</span>, about{" "}
                <span className="font-semibold">{(routeEstimate.minutes / 60).toFixed(1)} hr</span> one way
                {isRoundTrip ? "; doubled below for the round trip." : "."}
              </div>
            ) : (
              <Help>
                Pick both pickup and drop from the Google suggestions to fill these in automatically, or type them in.
              </Help>
            )
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-start">
            <div className="md:col-span-1" data-field="distanceKm">
              <Label>Distance (km)</Label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={v.distanceKm}
                onChange={(e) => setV((x) => ({ ...x, distanceKm: e.target.value }))}
                placeholder="e.g. 12.5"
                className={`${fieldClass(!!errors.distanceKm)} ${readOnly ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`}
                disabled={readOnly}
              />
              <FieldError msg={errors.distanceKm} />
            </div>

            <div className="md:col-span-1" data-field="durationHr">
              <Label>Duration (hrs)</Label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={v.durationHr}
                onChange={(e) => setV((x) => ({ ...x, durationHr: e.target.value }))}
                placeholder="e.g. 2.5"
                className={`${fieldClass(!!errors.durationHr)} ${readOnly ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`}
                disabled={readOnly}
              />
              <FieldError msg={errors.durationHr} />
            </div>

            {!readOnly && (
              <div className="md:col-span-2 space-y-1 md:pt-5">
                <button
                  type="button"
                  className="btn w-full"
                  disabled={distanceLoading || !pickupPinned || !dropPinned}
                  onClick={onRecalculate}
                  title={!pickupPinned || !dropPinned ? "Pick both pickup and drop on the map first" : undefined}
                >
                  {distanceLoading ? "Calculating..." : "Recalculate Distance"}
                </button>
                {distanceError && (
                  <div className="text-[11px] text-rose-600 dark:text-rose-400">Could not estimate the distance: {distanceError}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {initial && (v.dutySlip || v.parkingSlip) && (
        <Card
          title="Documents"
          subtitle="Duty slip and parking slip — upload from Invoice page"
          icon={<FiFileText className="w-5 h-5" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FileUploadComponent
                bookingId={v.id}
                fileType="dutySlip"
                onUpload={uploadDutySlip}
                existingFile={v.dutySlip}
                readOnly={true}
              />
            </div>
            <div>
              <FileUploadComponent
                bookingId={v.id}
                fileType="parkingSlip"
                onUpload={uploadParkingSlip}
                existingFile={v.parkingSlip}
                readOnly={true}
              />
            </div>
          </div>
        </Card>
      )}

      <Card
        title="Special Request / Travel Itinerary"
        subtitle="Anything else the driver should know"
        icon={<FiEdit3 className="w-5 h-5" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Special Request / Itinerary</Label>
            <textarea
              className={`w-full rounded-xl border border-slate-200 dark:border-slate-600
                         bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                         placeholder-slate-400 dark:placeholder-slate-500
                         px-3 py-2.5 text-sm min-h-28 focus:outline-none
                         focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-900/40
                         focus:border-emerald-500 dark:focus:border-emerald-500
                         ${readOnly ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}`}
              value={v.notes}
              onChange={set("notes")}
              placeholder="Any timing notes, waypoints, gate info, etc."
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>Trip will terminate at (optional)</Label>
            <I value={v.terminateAt} onChange={set("terminateAt")} placeholder="e.g. Client office, Sector 18" readOnly={readOnly} />
          </div>
          <div data-field="fare">
            <Label>Fare (manual, optional)</Label>
            <InputShell icon={<span>₹</span>}><I type="number" min="0" value={v.fare} onChange={set("fare")} placeholder="e.g. 1200" readOnly={readOnly} invalid={!!errors.fare} /></InputShell>
            {errors.fare ? (
              <FieldError msg={errors.fare} />
            ) : (
              <Help>Filled from the fare plan when one matches; you can overwrite it.</Help>
            )}
          </div>
          {activeTab === "individual" && (
            <div data-field="paymentMethod">
              <Label required>Payment Method</Label>
              <S value={v.paymentMethod} onChange={set("paymentMethod")} readOnly={readOnly} invalid={!!errors.paymentMethod}>
                <option value="">-- Select --</option>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </S>
              <FieldError msg={errors.paymentMethod} />
            </div>
          )}
        </div>

        {!readOnly && fareStatus?.state === "loading" && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <FiLoader className="w-4 h-4 animate-spin" /> Looking up the fare plan...
          </div>
        )}

        {!readOnly && fareStatus?.state === "error" && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-200">
            <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">The fare could not be filled in automatically</div>
              <div className="text-xs mt-0.5">{fareStatus.message}</div>
            </div>
          </div>
        )}

        {!readOnly && fareStatus?.state === "suggestion" && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
            <span>
              The fare plan for these choices is <span className="font-semibold">₹{fareStatus.total}</span>
              {isRoundTrip ? " (round trip)" : ""}. The fare entered above has been kept.
            </span>
            <button type="button" className="btn text-xs px-3 py-1.5" onClick={onApplyPlanFare}>
              Use plan fare ₹{fareStatus.total}
            </button>
          </div>
        )}

        {fareBreakdown && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">Fare Breakdown</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-blue-700 dark:text-blue-300">Base Fare:</span>
                <span className="font-medium">₹{fareBreakdown.breakdown.baseFare}</span>
              </div>
              {isRoundTrip && fareBreakdown.roundTrip && (
                <div className="flex justify-between">
                  <span className="text-blue-700 dark:text-blue-300">Round trip:</span>
                  <span className="font-medium">× 2</span>
                </div>
              )}
              {fareBreakdown.breakdown.extraKm > 0 && (
                <div className="flex justify-between">
                  <span className="text-blue-700 dark:text-blue-300">Extra KM ({fareBreakdown.breakdown.extraKm} km):</span>
                  <span className="font-medium">₹{fareBreakdown.breakdown.extraKmCharges}</span>
                </div>
              )}
              {fareBreakdown.breakdown.extraHr > 0 && (
                <div className="flex justify-between">
                  <span className="text-blue-700 dark:text-blue-300">Extra Hours ({fareBreakdown.breakdown.extraHr} hr):</span>
                  <span className="font-medium">₹{fareBreakdown.breakdown.extraHrCharges}</span>
                </div>
              )}
              <div className="border-t border-blue-200 dark:border-blue-700 pt-2 flex justify-between font-semibold">
                <span className="text-blue-900 dark:text-blue-100">Total:</span>
                <span className="text-blue-900 dark:text-blue-100">₹{fareBreakdown.totalFare}</span>
              </div>
            </div>
            <div className="mt-3 text-xs text-blue-600 dark:text-blue-400">
              <div>Package: {fareBreakdown.package?.name} ({fareBreakdown.package?.code})</div>
              <div>Enterprise: {fareBreakdown.enterprise?.name}</div>
              <div>Vehicle: {fareBreakdown.vehicle?.category} {fareBreakdown.vehicle?.model && `(${fareBreakdown.vehicle.model})`}</div>
              <div>Included: {fareBreakdown.breakdown.includedKm} km, {fareBreakdown.breakdown.includedHr} hr</div>
              {fareBreakdown.breakdown.extraPerKm > 0 && (
                <div>Extra Rates: ₹{fareBreakdown.breakdown.extraPerKm}/km, ₹{fareBreakdown.breakdown.extraPerHr}/hr</div>
              )}
              {fareBreakdown.breakdown.notes && (
                <div>Notes: {fareBreakdown.breakdown.notes}</div>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}
